import tinyColor from 'tinycolor2';

import deselectedColor from 'hooks/useChartDrillDowns/deselectedColor';

// How a chart's own values are styled (Studio.ChartStyle): the colours and
// geometry both renderers draw with, and the Highcharts side of it. The
// ECharts side is in StudioCharts/toEChartsOption.

// A line's fill fades from this opacity at the line to none at the axis;
// a solid fill is this opacity throughout
export const LINE_FILL_OPACITY = 0.35;
export const SOLID_FILL_OPACITY = 0.2;
// A fading bar is its full colour at the tip, this opacity at its base
export const BAR_BASE_OPACITY = 0.45;
// Highcharts' default padding: a category's bars take the middle 0.6 of
// it (groupPadding), each bar all but 0.1 of its share at either side
// (pointPadding)
const GROUP_HALF_WIDTH = 0.3;
const POINT_PADDING = 0.1;

// Half the width of a category's bars, where its track is drawn
const toTrackHalfWidth = (barCount: number): number =>
  GROUP_HALF_WIDTH -
  (POINT_PADDING * 2 * GROUP_HALF_WIDTH) / Math.max(1, barCount);

export const withOpacity = ({
  color,
  opacity,
}: {
  color: string;
  opacity: number;
}): string => tinyColor(color).setAlpha(opacity).toRgbString();

export const hasChartStyle = (style: Studio.ChartStyle): boolean =>
  Object.values(style).some(Boolean);

// The series fields read here, as the V5 gadgets build them (with their
// own role and colorIndex)
interface SeriesOptions {
  type?: string;
  name?: string;
  linkedTo?: string;
  role?: string;
  color?: unknown;
  colorIndex?: number;
  dashStyle?: string;
  data?: unknown[];
  [key: string]: unknown;
}

const LINE_TYPES = ['line', 'spline', 'area', 'areaspline'];
const COMPARISON_SUFFIX = ' - Last Period';

// Drawn from a metric's values (an average, a trend) rather than being them
const isDerived = (series: SeriesOptions): boolean =>
  !!series.linkedTo || series.role === 'average' || series.role === 'trend';

const isComparison = (series: SeriesOptions): boolean =>
  (series.name ?? '').endsWith(COMPARISON_SUFFIX) ||
  series.dashStyle === 'Dash';

const toSmooth = (type: string): string =>
  type === 'area' ? 'areaspline' : type === 'line' ? 'spline' : type;

const toFilled = (type: string): string =>
  type === 'spline' || type === 'areaspline' ? 'areaspline' : 'area';

const verticalGradient = ({
  color,
  from,
  to,
}: {
  color: string;
  from: number;
  to: number;
}): Highcharts.GradientColorObject => ({
  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
  stops: [
    [0, withOpacity({ color, opacity: from })],
    [1, withOpacity({ color, opacity: to })],
  ],
});

// A series' colour, as Highcharts would pick it
const getColor = ({
  series,
  index,
  colors,
}: {
  series: SeriesOptions;
  index: number;
  colors: string[];
}): string | undefined => {
  if (typeof series.color === 'string') {
    return series.color;
  }
  const colorIndex = series.colorIndex ?? index;
  return colors.length > 0 ? colors[colorIndex % colors.length] : undefined;
};

const styleLine = ({
  series,
  type,
  color,
  style,
  isFilledChart,
}: {
  series: SeriesOptions;
  type: string;
  color: string | undefined;
  style: Studio.ChartStyle;
  // An Area chart, whose series are always filled
  isFilledChart: boolean;
}): SeriesOptions => {
  const curved = style.curve === 'smooth' ? toSmooth(type) : type;
  const stepped = style.curve === 'step' ? { step: 'left' } : {};
  const fill = isComparison(series) ? undefined : style.lineFill;
  if (!fill) {
    return { ...series, type: curved, ...stepped } as SeriesOptions;
  }
  const fillColor =
    color && fill === 'gradient'
      ? verticalGradient({ color, from: LINE_FILL_OPACITY, to: 0 })
      : color
        ? withOpacity({ color, opacity: SOLID_FILL_OPACITY })
        : undefined;
  return {
    ...series,
    type: isFilledChart ? curved : toFilled(curved),
    ...stepped,
    ...(fillColor
      ? { fillColor }
      : { fillOpacity: fill === 'gradient' ? 0.15 : SOLID_FILL_OPACITY }),
  } as SeriesOptions;
};

// Bars fade from their tip to their base. Highcharts draws horizontal bars
// in a rotated group, so the same gradient runs along them there too.
const styleBar = ({
  series,
  color,
}: {
  series: SeriesOptions;
  color: string | undefined;
}): SeriesOptions => {
  const fade = (c: string) =>
    verticalGradient({ color: c, from: 1, to: BAR_BASE_OPACITY });
  const data = Array.isArray(series.data)
    ? series.data.map((point) => {
        const own =
          point && typeof point === 'object' && !Array.isArray(point)
            ? (point as { color?: unknown }).color
            : undefined;
        // A deselected (drilled past) bar stays grey
        return typeof own === 'string' && own !== deselectedColor
          ? { ...(point as object), color: fade(own) }
          : point;
      })
    : series.data;
  return {
    ...series,
    ...(color ? { color: fade(color) } : {}),
    data,
  } as SeriesOptions;
};

// A pale band behind each category's bars
const withTrack = ({
  options,
  categoryCount,
  barCount,
  color,
}: {
  options: Highcharts.Options;
  categoryCount: number;
  // Bars side by side in each category
  barCount: number;
  color: string;
}): Highcharts.Options['xAxis'] => {
  const halfWidth = toTrackHalfWidth(barCount);
  const bands: Highcharts.XAxisPlotBandsOptions[] = Array.from(
    { length: categoryCount },
    (_v, index) => ({
      from: index - halfWidth,
      to: index + halfWidth,
      color,
      className: 'studio-bar-track',
    }),
  );
  const addTo = (axis: Highcharts.XAxisOptions): Highcharts.XAxisOptions => ({
    ...axis,
    plotBands: [...(axis.plotBands ?? []), ...bands],
  });
  const { xAxis } = options;
  if (Array.isArray(xAxis)) {
    return xAxis.map((axis, index) => (index === 0 ? addTo(axis) : axis));
  }
  return addTo(xAxis ?? {});
};

// A chart's Highcharts options with its style applied to the series drawn
// from its metrics: comparisons follow the curve but aren't filled, and
// averages and trends are left as they are. Run before a forecast is added,
// so its series aren't restyled.
export const withChartStyle = ({
  options,
  style,
  trackColor,
}: {
  options: Highcharts.Options;
  style: Studio.ChartStyle;
  trackColor: string;
}): Highcharts.Options => {
  if (!hasChartStyle(style) || !options.series) {
    return options;
  }
  const chartType = (options.chart && options.chart.type) || 'line';
  const isBarChart = chartType === 'column' || chartType === 'bar';
  const isFilledChart = chartType === 'area';
  const colors = (options.colors ?? []).filter(
    (c): c is string => typeof c === 'string',
  );

  const series = (options.series as SeriesOptions[]).map((s, index) => {
    if (isDerived(s)) {
      return s;
    }
    const type = s.type ?? chartType;
    const color = getColor({ series: s, index, colors });
    if (LINE_TYPES.includes(type)) {
      return styleLine({ series: s, type, color, style, isFilledChart });
    }
    if (
      isBarChart &&
      style.isBarGradient &&
      (type === 'column' || type === 'bar')
    ) {
      return styleBar({ series: s, color });
    }
    return s;
  });

  // A smoothed Area chart's series are areasplines, which need the area
  // options (its stacking) too
  const plotOptions =
    isFilledChart && style.curve === 'smooth' && options.plotOptions
      ? {
          ...options.plotOptions,
          areaspline: {
            ...options.plotOptions.area,
            ...options.plotOptions.areaspline,
          } as Highcharts.PlotAreasplineOptions,
        }
      : options.plotOptions;

  const barSeries = series.filter(
    (s) => !isDerived(s) && !LINE_TYPES.includes(s.type ?? chartType),
  );
  const categoryCount = Math.max(
    0,
    ...barSeries.map((s) => (Array.isArray(s.data) ? s.data.length : 0)),
  );
  return {
    ...options,
    plotOptions,
    series: series as Highcharts.SeriesOptionsType[],
    ...(isBarChart && style.hasBarBackground && categoryCount > 0
      ? {
          xAxis: withTrack({
            options,
            categoryCount,
            barCount: barSeries.length,
            color: trackColor,
          }),
        }
      : {}),
  };
};
